import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import * as api from '@/api/endpoints'
import { queryKeys, type GiveawayCategory } from '@/api/query-keys'
import { useAuthStore } from '@/store/auth'
import { notifySuccess } from '@/lib/notify'
import type { Giveaway } from '@/models/giveaway'
import type { GameInfo } from '@/models/game-info'
import type { MediaUpload } from '@/models/media'
import type { User } from '@/models/user'

/**
 * The Vuex modules mixed three concerns: HTTP calls, cache state, and polling
 * timers (`SubscribeMyDetails` / `SubscribeFeaturedGiveaways` each installed a
 * `setInterval` that was never cleared and re-installed itself on every
 * dispatch). TanStack Query covers all three: `refetchInterval` replaces the
 * timers, and the cache replaces the mutation-managed module state.
 */

/* ------------------------------------------------------------------ me ---- */

export function useMe() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: api.getMyDetails,
    enabled: isAuthenticated,
    // The original polled /me every 30s to keep the loot-point counter fresh.
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
}

export function useMyLoot() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return useQuery({
    queryKey: queryKeys.myLoot,
    queryFn: api.getMyLoot,
    enabled: isAuthenticated,
  })
}

export function useMyGiveaways() {
  return useQuery({ queryKey: queryKeys.myGiveaways, queryFn: api.getMyGiveaways })
}

export function useMyMedias() {
  return useQuery({ queryKey: queryKeys.myMedias, queryFn: api.getMyMedias })
}

/** The platforms this account has linked (Discord, ...). */
export function useMyPlatforms() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return useQuery({
    queryKey: queryKeys.myPlatforms,
    queryFn: api.getMyPlatforms,
    enabled: isAuthenticated,
  })
}

/** Whether this account has linked `name`; `undefined` while still loading. */
export function useIsPlatformConnected(name: string): boolean | undefined {
  const { data } = useMyPlatforms()
  if (!data) return undefined
  return data.some((platform) => platform.name?.toLowerCase() === name && platform.connected)
}

export function useLinkDiscord() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => api.linkDiscord(code),
    onSuccess: (platforms) => {
      queryClient.setQueryData(queryKeys.myPlatforms, platforms)
      // The servers you manage come from the linked Discord account.
      void queryClient.invalidateQueries({ queryKey: queryKeys.managedGuilds })
      notifySuccess('Discord successfully connected')
    },
  })
}

export function useUpdateMyDetails() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (profile: Partial<User>) => api.updateMyDetails(profile),
    onSuccess: (user) => {
      queryClient.setQueryData(queryKeys.me, user)
      notifySuccess('Profile updated.')
    },
  })
}

/* -------------------------------------------------------------- enums ----- */

export function useGiveawayTypes() {
  return useQuery({
    queryKey: queryKeys.giveawayTypes,
    queryFn: api.getGiveawayTypes,
    staleTime: Infinity,
  })
}

export function usePrizeTypes() {
  return useQuery({
    queryKey: queryKeys.prizeTypes,
    queryFn: api.getPrizeTypes,
    staleTime: Infinity,
  })
}

export function useObjectives() {
  return useQuery({
    queryKey: queryKeys.objectives,
    queryFn: api.getObjectives,
    staleTime: Infinity,
  })
}

/* ----------------------------------------------------------- giveaways ---- */

/**
 * "All" merges every category the API exposes and de-duplicates by id, because
 * a giveaway is routinely in more than one of them (new *and* featured).
 */
const getAllGiveaways = async (page?: api.FeedPage): Promise<Giveaway[]> => {
  const lists = await Promise.all([
    api.getNewGiveaways(page),
    api.getFeaturedGiveaways(page),
    api.getEndingGiveaways(page),
  ])

  const byId = new Map<number, Giveaway>()
  for (const giveaway of lists.flat()) {
    if (giveaway.id !== null && !byId.has(giveaway.id)) byId.set(giveaway.id, giveaway)
  }
  // Soonest to finish first: the ones worth acting on now.
  return [...byId.values()].sort((a, b) => a.finishes_at.getTime() - b.finishes_at.getTime())
}

const categoryFetchers: Record<GiveawayCategory, (page?: api.FeedPage) => Promise<Giveaway[]>> = {
  all: getAllGiveaways,
  new: api.getNewGiveaways,
  featured: api.getFeaturedGiveaways,
  ending: api.getEndingGiveaways,
  // These two showed the "new" list until the API grew endpoints for them:
  // trending ranks live giveaways by entries in the last 24 hours, popular by
  // entries overall.
  trending: api.getTrendingGiveaways,
  popular: api.getPopularGiveaways,
}

/** Rows per request. The API's own default is 20; this is what a grid fills. */
export const FEED_PAGE_SIZE = 12

/**
 * A giveaway feed, paged against the API.
 *
 * The feeds used to return whole categories and the client paged what it had
 * already downloaded — fine for four rows, not for a production table. Pages
 * are fetched now, and `data` is still the flat list every caller expects, so
 * nothing above this had to change.
 *
 * A short page means the end: the API clamps `limit`, so asking for 12 and
 * getting 12 is the only reliable signal there may be more.
 */
export function useGiveawayCategory(category: GiveawayCategory) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.giveawayCategory(category),
    queryFn: ({ pageParam }) =>
      categoryFetchers[category]({ limit: FEED_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < FEED_PAGE_SIZE
        ? undefined
        : allPages.reduce((count, page) => count + page.length, 0),
    // Featured rotated on a 30-minute timer in the original.
    refetchInterval: category === 'featured' ? 30 * 60_000 : false,
    // Switching tabs keeps the previous list on screen until the next one
    // arrives, so the grid dims rather than collapsing and reflowing the page.
    placeholderData: keepPreviousData,
  })

  return {
    ...query,
    data: query.data?.pages.flat() ?? [],
    hasMore: query.hasNextPage,
    loadMore: () => void query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
  }
}

/** Entrants of a giveaway the signed-in user runs. Pass enabled only for its owner. */
export function useGiveawayEntrants(id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.giveawayEntrants(id ?? 0),
    queryFn: () => api.getGiveawayEntrants(id as number),
    enabled: enabled && id !== null,
    refetchOnWindowFocus: true,
  })
}

export function useGiveaway(id: string | number | undefined) {
  return useQuery({
    queryKey: queryKeys.giveaway(id ?? ''),
    queryFn: () => api.getGiveaway(id as string | number),
    enabled: id !== undefined && id !== '',
  })
}

/**
 * Entering a giveaway paints immediately and reconciles with the server after.
 *
 * The Quasar version awaited the round trip, then reached into the component's
 * own copy of the giveaway to flip `is_entered` and add to `my_entries` by
 * hand — so the button sat inert for the length of the request and any other
 * view of the same giveaway stayed stale.
 *
 * The cache is addressed by *record*, not by key: the same giveaway is cached
 * under its slug on `/g/:slug` and under its numeric id in the details overlay,
 * so patching only `queryKeys.giveaway(id)` would leave whichever one the user
 * is actually looking at untouched.
 */
export function useEnterGiveaway() {
  const queryClient = useQueryClient()

  /** Every cache entry holding this giveaway, whatever key it is filed under. */
  const entriesFor = (giveawayId: number) =>
    queryClient
      .getQueriesData<Giveaway>({ queryKey: ['giveaway'] })
      .filter((pair): pair is [readonly unknown[], Giveaway] => pair[1]?.id === giveawayId)

  return useMutation({
    mutationFn: api.enterGiveaway,

    onMutate: async ({ giveaway_id, giveaway_objective_id }) => {
      await queryClient.cancelQueries({ queryKey: ['giveaway'] })

      const previous = entriesFor(giveaway_id)

      for (const [key, giveaway] of previous) {
        const objective = giveaway.objectiveById(giveaway_objective_id)
        if (!objective || objective.is_entered) continue

        const next = giveaway.clone()
        // Replace rather than mutate: the objective is shared with `previous`,
        // which has to stay intact to roll back to.
        next.giveaway_objective = next.giveaway_objective.map((entry) =>
          entry.id === giveaway_objective_id
            ? Object.assign(Object.create(Object.getPrototypeOf(entry)), entry, {
                is_entered: true,
              })
            : entry,
        )
        next.my_entries += objective.entries_worth ?? 0
        // A hidden count this viewer can't see stays hidden.
        if (next.total_entries !== null || !next.hide_entries) {
          next.total_entries = (next.total_entries ?? 0) + 1
        }
        queryClient.setQueryData(key, next)
      }

      return { previous }
    },

    onError: (_error, _variables, context) => {
      for (const [key, giveaway] of context?.previous ?? []) {
        queryClient.setQueryData(key, giveaway)
      }
    },

    onSuccess: () => {
      notifySuccess('Successfully entered giveaway!')
    },

    onSettled: (_data, _error, variables) => {
      for (const [key] of entriesFor(variables.giveaway_id)) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })
}

export function useUpdateGiveaway() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (edit: api.GiveawayEdit) => api.updateGiveaway(edit),
    onSuccess: (giveaway, variables) => {
      queryClient.setQueryData(queryKeys.giveaway(variables.id), giveaway)
      if (giveaway.slug) queryClient.setQueryData(queryKeys.giveaway(giveaway.slug), giveaway)
      // The old slug now points at nothing we can trust.
      if (variables.slug !== giveaway.slug) {
        queryClient.removeQueries({ queryKey: queryKeys.giveaway(variables.slug) })
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGiveaways })
      // The home feeds, search and profile pages hold their own copies.
      void queryClient.invalidateQueries({ queryKey: ['giveaway', 'category'] })
      void queryClient.invalidateQueries({ queryKey: ['user'] })
      notifySuccess('Giveaway updated.')
    },
  })
}

/** Admin: put a giveaway on (or take it off) the home page's featured list. */
export function useSetGiveawayFeatured() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, featured }: { id: number; featured: boolean }) =>
      api.setGiveawayFeatured(id, featured),
    onSuccess: (giveaway, { id, featured }) => {
      queryClient.setQueryData(queryKeys.giveaway(id), giveaway)
      if (giveaway.slug) queryClient.setQueryData(queryKeys.giveaway(giveaway.slug), giveaway)
      void queryClient.invalidateQueries({ queryKey: ['giveaway', 'category'] })
      notifySuccess(featured ? 'Giveaway featured.' : 'Giveaway no longer featured.')
    },
  })
}

export function useCreateGiveaway() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (giveaway: Giveaway) => api.createGiveaway(giveaway),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['giveaway'] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGiveaways })
      // A giveaway can bring a new custom game with it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGames })
    },
  })
}

/* ---------------------------------------------------------- my games ---- */

export function useMyGames() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  return useQuery({
    queryKey: queryKeys.myGames,
    queryFn: api.getMyGames,
    enabled: isAuthenticated,
  })
}

export function useUpdateGameInfo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, game }: { id: number; game: GameInfo }) => api.updateGameInfo(id, game),
    onSuccess: (game) => {
      notifySuccess(`Saved ${game.name ?? 'your game'}`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.myGames })
      // Giveaways and search results embed the game.
      void queryClient.invalidateQueries({ queryKey: ['giveaway'] })
      void queryClient.invalidateQueries({ queryKey: ['game-info'] })
    },
  })
}

/* --------------------------------------------------------------- users ---- */

export function useUser(username: string | undefined) {
  return useQuery({
    queryKey: queryKeys.user(username ?? ''),
    queryFn: () => api.getUser(username as string),
    enabled: !!username,
    staleTime: 60_000,
  })
}

export function useUserGiveaways(username: string | undefined) {
  return useQuery({
    queryKey: queryKeys.userGiveaways(username ?? ''),
    queryFn: () => api.getUserGiveaways(username as string),
    enabled: !!username,
  })
}

/* ----------------------------------------------------------- game info ---- */

export function useGameInfoAutocomplete(query: string) {
  return useQuery({
    queryKey: queryKeys.gameInfoAutocomplete(query),
    queryFn: () => api.autocompleteGameInfo(query),
    enabled: query.trim().length >= 2,
    staleTime: 60_000,
    placeholderData: (previous: GameInfo[] | undefined) => previous,
  })
}

export function useGameInfoSearch(query: string) {
  return useQuery({
    queryKey: queryKeys.gameInfoSearch(query),
    queryFn: () => api.searchGameInfo(query),
    enabled: query.trim().length >= 2,
    staleTime: 60_000,
  })
}

/* --------------------------------------------------------------- media ---- */

export function useUploadMedia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (media: MediaUpload) => api.uploadMedia(media),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myMedias })
    },
  })
}

export function useUpdateMedia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; name: string; tags: string[] }) =>
      api.updateMedia(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myMedias })
      notifySuccess('Image saved')
    },
  })
}

export function useDeleteMedia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.deleteMedia(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.myMedias })
      notifySuccess('Image deleted')
    },
  })
}

/* ------------------------------------------------------------- discord ---- */

export function useManagedGuilds({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.managedGuilds,
    queryFn: api.getManagedGuilds,
    enabled,
    // Member counts come live from Discord, so refetch whenever the list is shown.
    staleTime: 0,
  })
}

export function useRegisterGuild() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.registerGuild,
    onSuccess: (guild) => {
      notifySuccess(`Authorized Discord server: ${guild.name ?? 'unknown'}`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.managedGuilds })
    },
  })
}

/* ---------------------------------------------------------------- auth ---- */

export function useDiscordOAuth() {
  const queryClient = useQueryClient()
  const setToken = useAuthStore((state) => state.setToken)

  return useMutation({
    mutationFn: (code: string) => api.oauthDiscord(code),
    onSuccess: ({ token, user }) => {
      setToken(token)
      queryClient.setQueryData(queryKeys.me, user)
      void queryClient.invalidateQueries({ queryKey: queryKeys.myPlatforms })
      notifySuccess('Discord successfully connected')
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  const setToken = useAuthStore((state) => state.setToken)

  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      api.login(username, password),
    onSuccess: ({ token, user }) => {
      setToken(token)
      queryClient.setQueryData(queryKeys.me, user)
      notifySuccess('Successfully logged in.')
    },
  })
}

/**
 * Whether a username is free, for the sign-up form. Pass an already-debounced
 * value. Failures stay quiet: the check is a convenience, and /auth/register
 * runs it again on submit.
 */
export function useUsernameAvailability(username: string) {
  const trimmed = username.trim()
  return useQuery({
    queryKey: queryKeys.usernameAvailable(trimmed),
    queryFn: () => api.checkUsernameAvailable(trimmed),
    enabled: trimmed.length > 0,
    staleTime: 30_000,
    retry: false,
    meta: { silentError: true },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  const setToken = useAuthStore((state) => state.setToken)

  return useMutation({
    mutationFn: (body: api.RegisterRequest) => api.register(body),
    onSuccess: ({ token, user }) => {
      setToken(token)
      queryClient.setQueryData(queryKeys.me, user)
      notifySuccess('Successfully logged in.')
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const clear = useAuthStore((state) => state.clear)

  return () => {
    clear()
    queryClient.clear()
    notifySuccess('Logged out.')
  }
}
